import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTrigger } from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Menu, Home, Search, Heart, ShoppingBag, User, HelpCircle, Globe, Shield, Store, Truck } from 'lucide-react';
import LumzaLogo from './LumzaLogo';
import { useAuth } from '@/contexts/AuthContext';

interface MobileNavDrawerProps {
  currentLanguage: string;
  onLanguageChange: (lang: string) => void;
}

const MobileNavDrawer = ({
  currentLanguage,
  onLanguageChange
}: MobileNavDrawerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, hasRole } = useAuth();

  const languages = [
    { code: 'EN', name: 'English', flag: '🇬🇧' },
    { code: 'ZU', name: 'Zulu', flag: '🇿🇦' },
    { code: 'XH', name: 'Xhosa', flag: '🇿🇦' },
    { code: 'ZH', name: '中文', flag: '🇨🇳' },
  ];

  const menuItems = [
    { icon: Home, label: 'Home', href: '/' },
    { icon: Search, label: 'Browse', href: '/browse' },
    { icon: Heart, label: 'Favorites', href: '/favorites' },
    { icon: ShoppingBag, label: 'Orders', href: '/orders' },
    { icon: User, label: 'Profile', href: '/profile' },
    { icon: HelpCircle, label: 'Help Center', href: '/help' },
  ];

  // Role-based dashboard links
  const dashboardItems: { icon: any; label: string; href: string }[] = [];
  if (user && (hasRole('admin') || hasRole('superadmin'))) {
    dashboardItems.push({ icon: Shield, label: 'Admin Dashboard', href: '/admin/dashboard' });
  }
  if (user && (hasRole('vendor') || hasRole('superadmin'))) {
    dashboardItems.push({ icon: Store, label: 'Vendor Dashboard', href: '/vendor/dashboard' });
  }
  if (user && (hasRole('driver') || hasRole('superadmin'))) {
    dashboardItems.push({ icon: Truck, label: 'Driver Dashboard', href: '/driver' });
  }

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="sm" className="p-2 hover:bg-primary/10 rounded-lg transition-colors" aria-label="Open navigation menu">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>

      <SheetContent side="left" className="w-80 bg-background border-r border-border/40 p-0">
        <div className="flex flex-col h-full">
          {/* Header */}
          <SheetHeader className="p-6 border-b border-border/40">
            <div className="flex items-center justify-between">
              <Link to="/" onClick={() => setIsOpen(false)}>
                <LumzaLogo size="md" />
              </Link>
            </div>
          </SheetHeader>

          {/* Scrollable Content */}
          <ScrollArea className="flex-1">
            {/* User Section */}
            <div className="p-6 bg-dark-surface/50">
              {user ? (
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                    <User className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{user.user_metadata?.full_name || 'Welcome!'}</p>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Welcome!</p>
                      <p className="text-sm text-muted-foreground">Sign in for better experience</p>
                    </div>
                  </div>
                  <Link to="/auth" className="w-full mt-4 block" onClick={() => setIsOpen(false)}>
                    <Button className="w-full btn-neon" size="sm">
                      Sign In / Register
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Dashboard Links (role-based) */}
            {dashboardItems.length > 0 && (
              <div className="py-4 border-b border-border/40">
                <p className="px-6 text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Dashboards</p>
                <ul className="space-y-1 px-6">
                  {dashboardItems.map(item => {
                    const Icon = item.icon;
                    return (
                      <li key={item.label}>
                        <Link
                          to={item.href}
                          className="flex items-center space-x-3 p-3 rounded-lg hover:bg-primary/10 transition-colors group"
                          onClick={() => setIsOpen(false)}
                        >
                          <Icon className="h-5 w-5 text-primary" />
                          <span className="text-foreground group-hover:text-primary transition-colors font-medium">
                            {item.label}
                          </span>
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* Navigation Menu */}
            <nav className="py-4">
              <ul className="space-y-1 px-6">
                {menuItems.map(item => {
                  const Icon = item.icon;
                  return (
                    <li key={item.label}>
                      <Link
                        to={item.href}
                        className="flex items-center space-x-3 p-3 rounded-lg hover:bg-primary/10 transition-colors group"
                        onClick={() => setIsOpen(false)}
                      >
                        <Icon className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                        <span className="text-foreground group-hover:text-primary transition-colors">
                          {item.label}
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Language Selector */}
            <div className="p-6 border-t border-border/40">
              <div className="flex items-center space-x-2 mb-3">
                <Globe className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-foreground">Language</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {languages.map(lang => (
                  <Button
                    key={lang.code}
                    variant={currentLanguage === lang.code ? "default" : "outline"}
                    size="sm"
                    className={`justify-start ${currentLanguage === lang.code ? 'bg-primary text-primary-foreground' : 'btn-ghost-neon'}`}
                    onClick={() => {
                      onLanguageChange(lang.code);
                      setIsOpen(false);
                    }}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </Button>
                ))}
              </div>
            </div>
          </ScrollArea>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default MobileNavDrawer;
